import { fetchAuthSession } from 'aws-amplify/auth';
import { importStorageKey, markImportRowSucceeded, prepareImportState } from './DirectOrderImportRetryState';
import type { StoredImports } from './DirectOrderImportRetryState';

function stable(value: any): any {
    if (Array.isArray(value)) return value.map(stable);
    if (value && typeof value === 'object') return Object.keys(value).sort().reduce((result, key) => {
        if (value[key] !== undefined) result[key] = stable(value[key]);
        return result;
    }, {} as Record<string, any>);
    return value;
}

async function fingerprint(value: unknown): Promise<string> {
    const bytes = new TextEncoder().encode(JSON.stringify(stable(value)));
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function userScope(): Promise<string> {
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString();
    if (!token) throw new Error('Sign in again before importing orders');
    const payload = token.split('.')[1];
    const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    if (typeof claims.sub !== 'string' || claims.sub.length === 0) throw new Error('Could not identify the signed-in customer');
    return claims.sub;
}

function readStored(key: string): StoredImports {
    const value = localStorage.getItem(key);
    if (!value) return { jobs: [] };
    const parsed = JSON.parse(value) as StoredImports;
    return { jobs: Array.isArray(parsed.jobs) ? parsed.jobs : [] };
}

function writeStored(key: string, state: StoredImports): void {
    localStorage.setItem(key, JSON.stringify(state));
}

export const DirectOrderImportRetry = {
    async prepare(rows: Array<Record<string, any>>, requestFields: string[]): Promise<{ keys: string[]; succeeded: boolean[]; jobId: string }> {
        const owner = await userScope();
        const storageKey = importStorageKey(owner);
        const state = readStored(storageKey);
        const requests = rows.map((row) => requestFields.reduce((request, field) => {
            if (row[field] !== undefined) request[field] = row[field];
            return request;
        }, {} as Record<string, unknown>));
        const rowFingerprints = await Promise.all(requests.map(fingerprint));
        const importFingerprint = await fingerprint(rowFingerprints);
        const prepared = prepareImportState(state, importFingerprint, rowFingerprints, () => crypto.randomUUID());
        writeStored(storageKey, prepared.state);
        return { keys: prepared.keys, succeeded: prepared.succeeded, jobId: prepared.jobId };
    },

    async markSucceeded(jobId: string, rowIndex: number): Promise<void> {
        const owner = await userScope();
        const storageKey = importStorageKey(owner);
        const state = readStored(storageKey);
        writeStored(storageKey, markImportRowSucceeded(state, jobId, rowIndex));
    }
};
